import cs from "classnames";
import React from "react";
import { FormattedDate, FormattedMessage } from "react-intl";

import { genericMessages } from "src/messages/generic";

import "./Timeline.css";

const ItemColorContext = React.createContext<string | undefined>(undefined);

interface TimelineProps<V> {
	items: TimelineItem<V>[];
	renderActions?(value: V): React.ReactNode;
	renderContent(value: V): React.ReactNode;
}

export interface TimelineItem<V> {
	color?: string;
	timestamp: string;
	author?: string;
	label: string;
	value: V;
	deleted?: boolean;
	modified?: boolean;
	tags?: string[];
}

interface TimelineGroup<V> {
	timestamp: string;
	author?: string;
	items: TimelineGroupItem<V>[];
}

interface TimelineGroupItem<V> {
	color?: string;
	label: string;
	value: V;
	deleted?: boolean;
	modified?: boolean;
	tags?: string[];
}

export function Timeline<V>({
	items,
	renderActions,
	renderContent
}: TimelineProps<V>): React.ReactElement {
	const groups = useGroups(items);
	return (
		<div className="timeline">
			{groups.map(({ timestamp, author, items }, key) => (
				<div key={key} className="timeline-group">
					<div className="timeline-group__left">
						<div className="timeline-group__date">
							<FormattedDate
								value={timestamp}
								year="numeric"
								month="numeric"
								day="numeric"
								hour="numeric"
								minute="numeric"
								second="numeric"
							/>
						</div>
						{author && (
							<div className="timeline-group__author">
								{author}
							</div>
						)}
					</div>
					<div className="timeline-group__separator">
						<div className="timeline-group__dot" />
						<div className="timeline-group__root" />
					</div>
					<div className="timeline-group__right">
						{items.map(
							(
								{
									color,
									label,
									value,
									deleted,
									modified,
									tags = []
								},
								key
							) => (
								<div
									key={key}
									className={cs("timeline-item", {
										"timeline-item--modified": modified,
										"timeline-item--deleted": deleted
									})}
								>
									<div
										className="timeline-item__header"
										style={
											color === undefined
												? undefined
												: { backgroundColor: color }
										}
									>
										<div className="timeline-item__label">
											{label}{" "}
											{modified && (
												<>
													{" - "}
													<FormattedMessage
														{...genericMessages.modified}
													/>
												</>
											)}
											{deleted && (
												<>
													{" - "}
													<FormattedMessage
														{...genericMessages.deleted}
													/>
												</>
											)}
										</div>
										{renderActions && (
											<div className="timeline-item__actions">
												{renderActions(value)}
											</div>
										)}
									</div>
									<ItemColorContext.Provider value={color}>
										<div
											className="timeline-item__content"
											style={
												color === undefined
													? undefined
													: { borderColor: color }
											}
										>
											{renderContent(value)}
											{tags.length !== 0 && (
												<div className="timeline-item__tags">
													{tags.map((tag, key) => (
														<div
															key={key}
															className="timeline-item__tag"
															style={
																color ===
																undefined
																	? undefined
																	: {
																			backgroundColor:
																				color
																		}
															}
														>
															{tag}
														</div>
													))}
												</div>
											)}
										</div>
									</ItemColorContext.Provider>
								</div>
							)
						)}
					</div>
				</div>
			))}
		</div>
	);
}

interface TimelineItemValueProps {
	value: React.ReactNode;
}

export const TimelineItemValue: React.FunctionComponent<
	TimelineItemValueProps
> = ({ value }) => {
	const color = React.useContext(ItemColorContext);
	const style = React.useMemo(
		() => (color === undefined ? undefined : { borderColor: color }),
		[color]
	);
	return (
		<div
			className={cs("timeline-item__value", {
				"timeline-item__value--string": typeof value === "string"
			})}
			style={style}
		>
			{value}
		</div>
	);
};

function useGroups<V>(items: TimelineItem<V>[]): TimelineGroup<V>[] {
	return React.useMemo(() => groupItems(items), [items]);
}

function groupItems<V>(items: TimelineItem<V>[]): TimelineGroup<V>[] {
	const map = new Map<string, TimelineItem<V>[]>();
	for (const item of items) {
		const key = `${item.timestamp}-${item.author}`;
		const group = map.get(key);
		if (group === undefined) {
			map.set(key, [item]);
		} else {
			group.push(item);
		}
	}
	const groups: TimelineGroup<V>[] = [];
	for (const items of map.values()) {
		const { timestamp, author } = items[0];
		groups.push(createGroup(timestamp, author, items));
	}
	return groups;
}

function createGroup<V>(
	timestamp: string,
	author: string | undefined,
	items: TimelineItem<V>[]
): TimelineGroup<V> {
	return { timestamp, author, items: items.map(stripItem) };
}

function stripItem<V>({
	timestamp,
	...item
}: TimelineItem<V>): TimelineGroupItem<V> {
	return item;
}
